<template>
  <Modal
    v-model:visible="isVisible"
    class="split-ship-order-modal"
    :header="t('shipOrders.shipOrderSplit.split')"
    @keypress.enter="onSubmit"
    @keypress.esc="onCancel"
  >
    <Card class="split-ship-order-card">
      <template #content>
        <div class="top-row">
          <h3>
            {{
              t('shipOrders.shipOrderNumParam', {
                param: shipOrder.compositeId,
              })
            }}
          </h3>
        </div>

        <div class="location-row">
          <div class="shipping-point-info origin">
            <FontAwesomeIcon
              :icon="['fas', 'map-marker-alt']"
              aria-hidden="true"
              class="text-pickup"
            />
            <div>
              <NamedAddress
                :name="shipOrder.origin.location.locationName"
                :address="shipOrder.origin.location.toAddress()"
                suppress-address-lines
                supress-postal-code
              />
              <div class="shipping-point-info-appt">
                {{
                  renderDateTime(
                    shipOrder.origin.requestedAppointmentEarliest,
                    { timeFormat: 'NONE' },
                  )
                }}
              </div>
            </div>
          </div>

          <i class="pi pi-arrow-right" aria-hidden="true" />

          <div class="shipping-point-info destination">
            <FontAwesomeIcon
              :icon="['fas', 'map-marker-alt']"
              aria-hidden="true"
              class="text-dropoff"
            />
            <div>
              <NamedAddress
                :name="shipOrder.destination.location.locationName"
                :address="shipOrder.destination.location.toAddress()"
                suppress-address-lines
                supress-postal-code
              />
              <div class="shipping-point-info-appt">
                {{
                  renderDateTime(
                    shipOrder.destination.requestedAppointmentEarliest,
                    { timeFormat: 'NONE' },
                  )
                }}
              </div>
            </div>
          </div>
        </div>

        <DataTable :value="lineItemSplits" class="line-items">
          <Column :header="t('shipOrders.shipOrderSplit.qtyOriginal')">
            <template #body="{ data: { shipOrderLineItem } }: DataTableRow">
              {{
                shipOrderLineItem.quantity +
                  ' ' +
                  shipOrderLineItem.handlingUnit?.name || ''
              }}
            </template>
          </Column>

          <Column :header="t('shipOrders.shipOrderSplit.qtyToKeep')">
            <template #body="{ data: lineItemSplit }: DataTableRow">
              <InputNumber
                v-model="lineItemSplit.qtyToKeep"
                :min="0"
                :max="lineItemSplit.shipOrderLineItem.quantity"
                show-buttons
                @value-change="onChangeAmount"
              />
            </template>
          </Column>

          <Column :header="t('shipOrders.shipOrderSplit.qtyOnNew')">
            <template #body="{ data: lineItemSplit }: DataTableRow">
              <InputNumber
                v-model="lineItemSplit.qtyOnNew"
                :min="0"
                :max="lineItemSplit.shipOrderLineItem.quantity"
                show-buttons
                @value-change="onChangeAmount"
              />
            </template>
          </Column>

          <Column
            field="shipOrderLineItem.description"
            :header="t('lineItems.lineItems')"
            header-style="min-width: 200px;"
          />
        </DataTable>
      </template>
    </Card>

    <InputError v-if="displayedError" :error="displayedError" />

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-arrows-h"
        :label="t('shipOrders.shipOrderSplit.split')"
        :disabled="!!displayedError"
        @click="onSubmit"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderDateTime } from '@rygen/rygen-ui/i18n';
import { Optional } from '@rygen/rygen-ui/model';

import { ShipOrder } from '@/model';
import {
  ShipOrderLineItemSplitBuilder,
  SplitShipOrderPayload,
  ValidShipOrder,
} from '@/model/routeBuilder';

import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputError from '@rygen/rygen-ui/components/form/InputError.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Card from 'primevue/card';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';

export type DataTableRow = {
  data: ShipOrderLineItemSplitBuilder;
  index: number;
};

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipOrder: {
    type: Object as PropType<ShipOrder>,
    default: () => new ShipOrder(),
  },
});
const { shipOrder } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'submit', payload: SplitShipOrderPayload): void;
}>();

const { t } = useI18n();

const lineItemSplits = ref(
  shipOrder.value.lineItems.map((li) => new ShipOrderLineItemSplitBuilder(li)),
);

const error: Ref<Optional<ErrorObject>> = ref();

const hasNestedLineItems = computed(() =>
  lineItemSplits.value.some(
    (split) => !!split.shipOrderLineItem.childLineItems?.length,
  ),
);

const nestedLineItemsError = computed<Optional<ErrorObject>>(() =>
  hasNestedLineItems.value
    ? {
        $uid: 'nested-line-items',
        $message: t('shipOrders.shipOrderSplit.errors.nestedLineItems'),
        $propertyPath: 'nested-line-items',
        $property: 'ship-orders',
        $validator: 'manual',
        $params: {},
        $pending: false,
        $response: undefined,
      }
    : undefined,
);

const displayedError = computed(
  () => nestedLineItemsError.value ?? error.value,
);

const onChangeAmount = () => {
  error.value = undefined;
};

const validateSplits = (): boolean => {
  const isKeepingAll = lineItemSplits.value.every((l) => l.qtyOnNew === 0);
  const isMovingAll = lineItemSplits.value.every((l) => l.qtyToKeep === 0);

  if (isKeepingAll || isMovingAll) {
    error.value = {
      $uid: isKeepingAll ? 'keeping-all' : 'moving-all',
      $message: isKeepingAll
        ? t('shipOrders.shipOrderSplit.errors.keepingAll')
        : t('shipOrders.shipOrderSplit.errors.movingAll'),
      $propertyPath: isKeepingAll ? 'keeping-all' : 'splitting-all',
      $property: 'ship-orders',
      $validator: 'manual',
      $params: {},
      $pending: false,
      $response: undefined,
    };
    return false;
  }

  error.value = undefined;
  return true;
};

const onSubmit = () => {
  const isValid = validateSplits();
  if (!isValid) {
    return;
  }

  const validShipOrder = ValidShipOrder.isValidShipOrder(shipOrder.value)
    ? shipOrder.value
    : new ValidShipOrder(shipOrder.value);

  const payload: SplitShipOrderPayload = {
    shipOrder: validShipOrder,
    splitLineItems: lineItemSplits.value.map((li) => ({
      shipOrderLineItemId: li.shipOrderLineItem.id,
      qtyToKeep: li.qtyToKeep,
      qtyOnNew: li.qtyOnNew,
    })),
  };

  isVisible.value = false;
  emit('submit', payload);
};

const onCancel = () => {
  isVisible.value = false;
  emit('cancel');
};

watch(
  shipOrder,
  () => {
    lineItemSplits.value = shipOrder.value.lineItems.map(
      (li) => new ShipOrderLineItemSplitBuilder(li),
    );
  },
  { deep: true },
);

watch(
  isVisible,
  () => {
    error.value = undefined;
  },
  { deep: true },
);
</script>

<style>
.split-ship-order-modal.p-dialog.rygen-modal {
  --rygen-modal-max-width: 40vw;
  --rygen-modal-width: 40vw;

  .p-dialog-content {
    padding-top: 0;

    .split-ship-order-card {
      .p-card-body {
        padding: 0;

        .top-row {
          background-color: var(--p-gray-200);
          padding: var(--sp-3);

          h3 {
            font-weight: 700;
          }
        }

        .location-row {
          display: flex;
          gap: var(--gap-1);
          justify-content: space-between;
          align-items: center;
          padding: var(--sp-3);

          .shipping-point-info {
            display: flex;
            gap: var(--sp-1);
          }
        }

        .line-items.p-datatable {
          .p-datatable-table {
            .p-datatable-thead {
              > tr {
                > th {
                  background: initial;
                  font-size: 10px;
                }
              }
            }
          }

          .p-inputnumber {
            max-width: 100px;
          }
        }
      }
    }
  }
}
</style>
